/**
 * ============================================================================
 * Theme state — the runtime half of the token system (D-034).
 * ============================================================================
 *
 * The tokens themselves live in `src/global/theme/tokens.css`. This module owns
 * exactly one piece of state: which theme is active, expressed as
 * `data-theme` on <html>.
 *
 * `<html data-theme>` IS THE STATE. Not a React ref, not a module variable —
 * the attribute the inline boot script in `public/index.html` has already set
 * before React exists. Reading it back is what lets the toggle mount in the
 * right position with no second paint. Two sources of truth here would mean a
 * visible flip on hydration.
 *
 * Persistence is a cookie, deliberately. `localStorage` is unavailable in some
 * of the environments this repo is rendered in, and a cookie is also the only
 * option that would survive the CRA → Vite SSG migration (D-003) intact: it is
 * readable by a server renderer, which `localStorage` never is. `SameSite=Lax`
 * because nothing cross-site needs it; no `Secure` flag, because that would
 * drop the cookie on the plain-HTTP local harness the verification runs
 * against, and a theme preference is not a secret.
 */

export type ThemeName = "dark" | "light";

/** Dark is the default and the site's signature identity (D-034). */
export const DEFAULT_THEME: ThemeName = "dark";

const COOKIE_NAME = "theme";
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

const isTheme = (value: string | null | undefined): value is ThemeName => value === "dark" || value === "light";

/** The persisted preference, or null when the visitor has never chosen. */
export const readThemeCookie = (): ThemeName | null => {
	const match = document.cookie.match(/(?:^|;\s*)theme=(dark|light)(?:;|$)/);
	return match && isTheme(match[1]) ? match[1] : null;
};

/**
 * The theme currently painted, read off <html>.
 *
 * Falls back to the default rather than to `prefers-color-scheme`: if the
 * attribute is missing the boot script did not run, and guessing a *different*
 * answer here would produce the flash the boot script exists to prevent.
 */
export const getActiveTheme = (): ThemeName => {
	const attr = document.documentElement.getAttribute("data-theme");
	return isTheme(attr) ? attr : DEFAULT_THEME;
};

/** Apply, persist, and mirror into the browser chrome. One call does all three. */
export const applyTheme = (theme: ThemeName): void => {
	document.documentElement.setAttribute("data-theme", theme);
	document.cookie = `${COOKIE_NAME}=${theme}; path=/; max-age=${ONE_YEAR_SECONDS}; SameSite=Lax`;

	// Read the chrome colour back out of the cascade rather than repeating the
	// hex here: tokens.css stays the single source, and this cannot drift.
	const chrome = getComputedStyle(document.documentElement).getPropertyValue("--chrome-color").trim();
	// Unquoted attribute value: `theme-color` is a valid CSS identifier, so this
	// needs no inner quotes — which keeps the eslint `quotes` rule satisfied
	// without escaping.
	const meta = document.querySelector("meta[name=theme-color]");
	if (meta && chrome) meta.setAttribute("content", chrome);
};

export const nextTheme = (theme: ThemeName): ThemeName => (theme === "dark" ? "light" : "dark");
